import { ATTACHMENT_UPLOAD_URL, DEFAULT_HEADERS, SFDC_CHECK_USER_URL } from './config';

const MAX_ATTACHMENT_BYTES = 4 * 1024 * 1024;

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

/**
 * Upload a file to the live agent's Salesforce case for the given conversation.
 * @param {object} params
 * @param {string} params.CaseID - Salesforce case ID.
 * @param {File} params.file - The file selected by the user.
 * @param {string} params.inContactSessionID - DirectLine conversation id (activity.conversation.id).
 * @returns {Promise<object>} The upload response from the backend.
 */
export async function uploadAttachment({ CaseID, file, inContactSessionID }) {
  if (file.size > MAX_ATTACHMENT_BYTES) {
    throw new Error('File exceeds the 4 MB limit.');
  }
  const contentBase64 = await fileToBase64(file);
  const response = await fetch(ATTACHMENT_UPLOAD_URL, {
    method: 'POST',
    headers: DEFAULT_HEADERS,
    body: JSON.stringify({ CaseID, inContactSessionID, fileName: file.name, contentBase64 }),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) {
    throw new Error(data.message || data.detail || `HTTP ${response.status}`);
  }

  return data;
}

/**
 * Check whether a user exists in Salesforce (SFDC) and fetch their Titan Portal
 * registration record, keyed by email.
 * @param {string} email - The customer's email address to look up.
 * @returns {Promise<object>} The SFDC query response, e.g. { is_registered, totalSize, contact }.
 */
export async function checkSfdcUser(email) {
  if (!email) {
    throw new Error('Email is required to check SFDC registration status');
  }

  const response = await fetch(`${SFDC_CHECK_USER_URL}?email=${encodeURIComponent(email)}`, {
    method: 'GET',
    headers: DEFAULT_HEADERS,
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return response.json();
}
